import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { T } from '@tolgee/react';
import { claimsFaqAnswer } from '../content/claimsPolicy';

type FaqItem = {
  id: string;
  question: string;
  answer: string;
};

type FaqCategory = {
  id: string;
  title: string;
  questions: FaqItem[];
};

const FAQ = () => {
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [expandedQuestions, setExpandedQuestions] = useState<string[]>([]);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [searchTerm, setSearchTerm] = useState("");

  const faqCategories: FaqCategory[] = [
    {
      id: "acceso",
      title: "Acceso a la Tienda Online",
      questions: [
        {
          id: "a1",
          question: "¿Quién puede acceder a la tienda online de Jarad Pet Food?",
          answer: "La tienda online está destinada exclusivamente a profesionales del sector: distribuidores, mayoristas y tiendas especializadas. No está disponible para particulares."
        },
        {
          id: "a2",
          question: "¿Cómo solicito el acceso a la tienda?",
          answer: "Debes rellenar el formulario de \"Solicitar alta\" desde la web. Nuestro equipo verificará los datos y, una vez validado, crearemos tu usuario y te enviaremos las credenciales de acceso."
        },
        {
          id: "a3",
          question: "¿Cuánto tarda la validación de mi usuario?",
          answer: "La validación suele completarse en un plazo de 24 a 48 horas hábiles tras la recepción del formulario."
        },
        {
          id: "a4",
          question: "¿Puedo registrarme como particular?",
          answer: "No. El acceso a la tienda online está reservado exclusivamente para distribuidores, mayoristas y tiendas especializadas del sector pet food."
        },
        {
          id: "a5",
          question: "¿Puedo registrarme directamente sin validación?",
          answer: "No. El acceso a la tienda requiere validación previa por parte de Jarad Pet Food, ya que trabajamos únicamente con clientes profesionales activos."
        },
        {
          id: "a6",
          question: "He olvidado mi contraseña. ¿Qué hago?",
          answer: "Haz clic en \"¿Has olvidado tu contraseña?\" en el acceso a la tienda y sigue las instrucciones para restablecerla."
        }
      ]
    },
    {
      id: "compra",
      title: "Condiciones de Compra",
      questions: [
        {
          id: "c1",
          question: "¿Cuál es el pedido mínimo?",
          answer: "El pedido mínimo es de 1 saco o 1 caja de paquetería."
        },
        {
          id: "c2",
          question: "¿Puedo combinar diferentes productos en un mismo pedido?",
          answer: "Sí, puedes combinar diferentes productos de nuestro catálogo en el mismo palé."
        },
        {
          id: "c3",
          question: "¿Se muestran los precios en la web?",
          answer: "Los precios son visibles solo para usuarios registrados y dependen de la tarifa comercial asignada a cada cliente."
        },
        {
          id: "c4",
          question: "¿Los precios incluyen descuentos?",
          answer: "Los precios mostrados son netos de tarifa. Los descuentos comerciales se aplican según el acuerdo con cada cliente."
        },
        {
          id: "c5",
          question: "¿Puedo modificar un pedido realizado a través de la tienda online?",
          answer: "Sí. Podrás solicitar modificaciones durante las primeras 48 horas posteriores a la realización del pedido. Transcurrido este plazo, no será posible realizar ningún cambio. Para solicitar una modificación, ponte en contacto con nuestro equipo indicando el número de pedido."
        }
      ]
    },
    {
      id: "envios",
      title: "Envíos y Entregas",
      questions: [
        {
          id: "e1",
          question: "¿Cuál es el tiempo de entrega habitual?",
          answer: "El plazo de entrega es aproximadamente de 7 a 8 días hábiles desde la confirmación del pedido. Este plazo puede variar en función de la demanda de producción, la disponibilidad de producto y la zona de destino."
        },
        {
          id: "e2",
          question: "¿Puedo elegir el horario de entrega?",
          answer: "Sí. Recomendamos confirmar el día y horario preferente de descarga para evitar incidencias."
        },
        {
          id: "e3",
          question: "¿Qué hago si mi mercancía llega dañada?",
          answer: "Si detectas algún bulto roto o golpeado, anota en el albarán \"mercancía rota\". Si no se observa daño externo pero quieres revisarlo después, indica \"pendiente de revisar\"."
        },
        {
          id: "e4",
          question: "¿Qué tipo de transporte puede acceder a descargar?",
          answer: "Por favor, comunícanos qué tipo de camión puede entrar en tus instalaciones para evitar problemas en la entrega."
        }
      ]
    },
    {
      id: "productos",
      title: "Productos y Calidad",
      questions: [
        {
          id: "p1",
          question: "¿Qué tipo de productos ofrece Jarad Pet Food?",
          answer: "Ofrecemos alimentación natural y funcional para: Aves, Roedores, Perros y Gatos. A través de distintas líneas adaptadas a cada necesidad, como Jarad PREMIfood, Jarad La Mejor Selección, Jarad Active, Jarad Vital y Jarad Plus."
        },
        {
          id: "p2",
          question: "¿Los productos son adecuados para todas las etapas de vida?",
          answer: "Sí. Nuestras gamas están adaptadas por edad, tamaño y necesidades específicas."
        },
        {
          id: "p3",
          question: "¿Tienen fecha de caducidad o duración recomendada?",
          answer: "Sí. Todos los productos incluyen fecha de consumo preferente para garantizar su frescura y calidad."
        },
        {
          id: "p4",
          question: "¿Los productos cuentan con control veterinario?",
          answer: "Sí, todas las fórmulas de Jarad Pet Food son desarrolladas bajo supervisión veterinaria y con certificaciones de calidad ISO 9001, 14001 y 22000."
        },
        {
          id: "p5",
          question: "¿Dónde se fabrican los productos de aves y roedores?",
          answer: "Todos los productos se fabrican en nuestras instalaciones de El Coronil (Sevilla, España), bajo el grupo Valle Galbarro S.L., garantizando trazabilidad y calidad desde el origen en cuanto a los productos de aves y roedores."
        },
        {
          id: "p6",
          question: "¿Qué hace que Jarad Pet Food sea diferente de otras marcas?",
          answer: "• Producción y selección propias.\n• Ingredientes 100% naturales.\n• Más de 30 años de experiencia.\n• Gamas adaptadas a cada especie.\n• Compromiso con la sostenibilidad."
        }
      ]
    },
    {
      id: "incidencias",
      title: "Incidencias y Atención al Cliente",
      questions: [
        {
          id: "i1",
          question: "¿Cómo puedo gestionar una incidencia o devolución de un producto?",
          answer: claimsFaqAnswer
        },
        {
          id: "i3",
          question: "¿Cómo puedo actualizar mis datos de contacto o correo electrónico?",
          answer: "Puedes escribirnos directamente para actualizar tu email o teléfono, y así poder seguir recibiendo confirmaciones."
        }
      ]
    }
  ];

  // Filtrar preguntas
  const filteredQuestions = faqCategories
    .filter(cat => !selectedCategory || cat.id === selectedCategory)
    .flatMap(category => category.questions.map(q => ({ ...q, categoryId: category.id })))
    .filter(item => {
      if (!searchTerm) return true;
      return item.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.answer.toLowerCase().includes(searchTerm.toLowerCase());
    });

  const toggleQuestion = (id: string) => {
    setExpandedQuestions(prev =>
      prev.includes(id) ? prev.filter(q => q !== id) : [...prev, id]
    );
  };

  useEffect(() => {
    document.title = "Preguntas Frecuentes | Jarad Pet Food";
  }, []);

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#f2ede4' }}>
      {/* Hero section */}
      <section className="w-full py-24 flex flex-col items-center justify-center">
        <motion.h1
          className="text-4xl md:text-6xl font-extrabold tracking-tight mb-6 text-center"
          style={{ color: '#0d0d0d' }}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <T keyName="faq.title">Preguntas Frecuentes</T>
        </motion.h1>
        <motion.p
          className="text-xl md:text-2xl font-light max-w-3xl text-center px-6"
          style={{ color: '#6B7280' }}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          Encuentra respuestas a las preguntas más comunes sobre nuestros productos y servicios
        </motion.p>
      </section>

      {/* Buscador */}
      <section className="w-full py-12">
        <div className="content-container">
          <motion.div
            className="relative max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <input
              ref={searchInputRef}
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar preguntas frecuentes..."
              className="w-full px-6 py-4 text-lg border-2 border-gray-200 focus:border-gray-400 outline-none bg-white rounded-2xl font-light"
              style={{ color: '#0d0d0d' }}
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm("")}
                className="absolute right-6 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
            {!searchTerm && (
              <div className="absolute right-6 top-1/2 transform -translate-y-1/2 text-gray-400">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            )}
          </motion.div>
        </div>
      </section>

      {/* Categorías de preguntas */}
      <section className="w-full py-12">
        <div className="content-container">
          <motion.div
            className="flex flex-wrap justify-center gap-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            <button
              onClick={() => setSelectedCategory(null)}
              className={`px-6 py-3 text-lg font-medium rounded-full transition-all duration-300 ${selectedCategory === null
                  ? "text-white shadow-lg"
                  : "bg-white text-gray-600 hover:bg-gray-50"
                }`}
              style={{
                backgroundColor: selectedCategory === null ? '#f6cf02' : undefined,
                color: selectedCategory === null ? '#0d0d0d' : undefined
              }}
            >
              Todas
            </button>
            {faqCategories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`px-6 py-3 text-lg font-medium rounded-full transition-all duration-300 ${selectedCategory === category.id
                    ? "text-white shadow-lg"
                    : "bg-white text-gray-600 hover:bg-gray-50"
                  }`}
                style={{
                  backgroundColor: selectedCategory === category.id ? '#f6cf02' : undefined,
                  color: selectedCategory === category.id ? '#0d0d0d' : undefined
                }}
              >
                {category.title}
              </button>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Lista de preguntas frecuentes */}
      <section className="w-full py-16">
        <div className="content-container">
          <div className="max-w-4xl mx-auto">
            {filteredQuestions.length === 0 ? (
              <motion.div
                className="text-center py-12"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto mb-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <h3 className="text-2xl font-bold text-gray-600 mb-2" style={{ color: '#0d0d0d' }}>
                  No encontramos resultados
                </h3>
                <p className="text-gray-500">
                  Intenta con otra búsqueda o categoría
                </p>
              </motion.div>
            ) : (
              <div className="space-y-6">
                {filteredQuestions.map((item, index) => (
                  <motion.div
                    key={item.id}
                    className="bg-white rounded-2xl shadow-md overflow-hidden"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.05 }}
                  >
                    <button
                      onClick={() => toggleQuestion(item.id)}
                      className="w-full text-left flex justify-between items-start p-6 hover:bg-gray-50 transition-colors duration-200"
                    >
                      <h3 className="text-lg md:text-xl font-bold pr-8" style={{ color: '#0d0d0d' }}>
                        {item.question}
                      </h3>
                      <div className="mt-1 flex-shrink-0">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className={`h-6 w-6 transition-transform duration-300 ${expandedQuestions.includes(item.id) ? "rotate-45" : ""
                            }`}
                          style={{ color: '#f6cf02' }}
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                      </div>
                    </button>
                    <AnimatePresence>
                      {expandedQuestions.includes(item.id) && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3 }}
                          className="overflow-hidden"
                        >
                          <div className="px-6 pb-6 text-lg text-gray-600 font-light whitespace-pre-line">
                            {item.answer}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Sección de contacto */}
      <section className="w-full py-16">
        <div className="content-container">
          <div className="max-w-2xl mx-auto text-center">
            <motion.h2
              className="text-3xl md:text-4xl font-bold mb-6"
              style={{ color: '#0d0d0d' }}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              ¿No encontraste lo que buscabas?
            </motion.h2>
            <motion.p
              className="text-xl text-gray-600 font-light mb-12"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
            >
              Contáctanos directamente y responderemos a todas tus preguntas
            </motion.p>
            <motion.a
              href="mailto:info@jaradpetfood.com"
              className="inline-flex items-center px-8 py-4 rounded-full font-medium transition-all duration-300 border-2 shadow-md hover:shadow-lg"
              style={{
                backgroundColor: '#f6cf02',
                color: '#0d0d0d',
                borderColor: '#0d0d0d'
              }}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              viewport={{ once: true }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#0d0d0d';
                e.currentTarget.style.color = '#f6cf02';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#f6cf02';
                e.currentTarget.style.color = '#0d0d0d';
              }}
            >
              Contactar ahora
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
              </svg>
            </motion.a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FAQ;
